<template>
	<div
		v-if="category"
		class="expense-card"
		:class="cardClasses"
		role="listitem"
		@click="clickHandler"
	>
		<div class="overlay text--center">
			{{ $t('unavailableDashboardCategory') }}
		</div>
		<div class="locked-overlay text--center">
			<svgicon name="lock" class="icon--x-large icon--white"></svgicon>
		</div>
		<div class="expense-card__content">
			<div class="icon-wrapper">
				<div class="svg-wrapper" aria-hidden="true">
					<svgicon :name="`expenseCategories/${category.assetName}`"></svgicon>
				</div>
				<span
					v-if="isTagVisible(category)"
					class="tag tag--rounded tag--orange tag--sm"
					:class="{ 'tag--orange-inverted': cardClasses === 'locked' }"
				>
					{{ $t('new') }}
				</span>
			</div>
			<div class="title-wrapper">
				<div class="heading-3">
					{{ category.name }}
					<svgicon
						v-if="useIcon && statusIcon.isVisible"
						:name="statusIcon.name"
						:class="statusIcon.class"
					></svgicon>
				</div>
			</div>
			<div v-if="!category.isLockedForCurrentStudent" class="range-wrapper">
				<div class="heading-4" :name="category.name + ' paid'">
					{{ category.amountSpentForCurrentStudent | currency }}
				</div>
				<div class="body-text-2" :name="category.name + ' budgeted'">
					{{ categoryBudget | currency }}
				</div>
			</div>
			<div class="budget-bar-wrapper" :tutorial-id="useTutorialId">
				<ja-budget-bar
					v-if="!category.isLockedForCurrentStudent"
					aria-hidden="true"
					:value="category.amountSpentForCurrentStudent"
					:max-value="categoryBudget"
					:overflow="category.amountSpentForCurrentStudent > categoryBudget"
				>
				</ja-budget-bar>
				<ja-tooltip
					v-if="showTutorial"
					ref="tooltip"
					class="tooltip left"
					:pointer="true"
					tabindex="0"
					aria-activedescendant="text-tooltip-3"
				>
					<h1 id="text-tooltip-3" class="body-text-1">
						{{ $t('calendarОnboardingExpCat') }}
					</h1>
					<ja-button
						class="small transparent"
						name="drag-help"
						:aria-label="this.$t('goNextOnboardingStep')"
						@click.native="continueOnboarding"
					>
						{{ $t('next') }}
					</ja-button>
				</ja-tooltip>
			</div>
		</div>
	</div>
</template>
<script>
import BudgetBar from '../ui/BudgetBar';
import Button from '../ui/Button';
import Tooltip from '../ui/Tooltip';
import CategoryCheck from '@/mixins/CategoryCheck';

export default {
	components: {
		jaButton: Button,
		jaTooltip: Tooltip,
		jaBudgetBar: BudgetBar
	},
	mixins: [CategoryCheck],
	props: {
		category: {
			type: Object,
			default: null
		},
		showTutorial: {
			type: Boolean,
			default: false
		},
		useClickEvent: {
			type: Boolean,
			default: false
		},
		useTag: {
			type: Boolean,
			default: false
		},
		useIcon: {
			type: Boolean,
			default: false
		},
		useLock: {
			type: Boolean,
			default: false
		},
		useTutorialId: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			isIconVisible: false,
			statusIcon: {
				name: 'check',
				isVisible: false,
				class: ''
			}
		};
	},
	computed: {
		categoryBudget() {
			return this.category.studentExpenseCategoryBudgets.reduce(function(
				sum,
				item
			) {
				return sum + item.budget;
			},
			0);
		},
		cardClasses() {
			if (!this.useLock) {
				return '';
			}
			// disabled cards are with priority
			if (this.category.isLockedForCurrentStudent) {
				return 'disabled';
			} else if (this.category.isPasscodeLockedForCurrentStudent) {
				return 'locked';
			}

			return '';
		}
	},
	watch: {
		category: {
			handler() {
				this.updateStatusIcon();
			},
			deep: true
		}
	},
	updated() {
		if (this.showTutorial) {
			this.$refs.tooltip.$el.focus();
		}
	},
	mounted() {
		this.updateStatusIcon();
	},
	methods: {
		continueOnboarding() {
			this.$emit('continueOnboarding');
		},
		clickHandler() {
			if (this.useClickEvent) {
				this.$emit('click');
			}
		},
		isTagVisible(item) {
			const { isInBudgetingPhase, userData } = this.$store.getters;

			if (!this.useTag || !userData) {
				return false;
			}

			if (isInBudgetingPhase && userData.studentPreviousLifeStageId) {
				return !this.isCategoryBudgeted(item) && this.isCategoryEnabled(item);
			}

			return false;
		},
		updateStatusIcon() {
			const isCompletedOrBudgeted =
				this.category.isCompletedForCurrentStudent ||
				this.isCategoryBudgeted(this.category);

			this.statusIcon.name = isCompletedOrBudgeted ? 'check' : '';

			this.statusIcon.isVisible =
				this.isCategoryDisabled(this.category) || isCompletedOrBudgeted;

			this.statusIcon.class = isCompletedOrBudgeted ? 'icon--success' : '';
		}
	}
};
</script>
<style lang="scss" scoped>
.expense-card {
	height: 210px;
	position: relative;
	background-color: $container-bgcolor;
	border: 1px solid $container-border;
	border-radius: 6px;
	box-shadow: 0 15px 30px 0 rgba(0, 76, 208, 0.24);
	cursor: pointer;
	@media (min-width: $breakpoint-mid) {
		height: 250px;
	}

	&__content {
		height: inherit;
		padding: 24px 16px 32px 16px;
		display: flex;
		flex-direction: column;
	}

	&.disabled {
		box-shadow: 0 15px 30px 0 rgba(0, 76, 208, 0.1);
		.expense-card__content {
			opacity: 0.3;
		}

		&:hover {
			.overlay {
				font-family: 'RobotoMedium';
				font-weight: 500;
				background-color: rgba($checkbox-icon-color, 0.14);
				display: flex;
				cursor: not-allowed;
				padding: 8px;
			}
		}
	}

	.overlay,
	.locked-overlay {
		display: none;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
		position: absolute;
		z-index: 1;
		border-radius: 6px;
	}

	&.locked {
		.locked-overlay {
			display: flex;
			background-color: rgba($font-gray, 0.5);
			svg {
				width: 52px;
				height: 52px;
			}
		}
	}

	.svg-wrapper {
		svg {
			height: 24px;
			width: 24px;
			fill: $icon-disabled;
		}
	}

	.icon-wrapper {
		display: flex;
		justify-content: space-between;
	}

	.title-wrapper {
		flex-grow: 1;
	}

	.range-wrapper {
		display: flex;
		align-items: center;

		.heading-4 {
			flex-grow: 1;
			display: inline-block;
			margin: 0;
			color: $font-blue;
		}

		.body-text-2 {
			display: inline-block;
			color: $font-dark;
		}

		.cart-cost {
			background-color: red;
			.filled {
				background-color: $primary-state;
			}
		}
	}

	.tag {
		z-index: 9;
	}

	.budget-bar-wrapper {
		position: relative;
		.tooltip {
			z-index: 99;
			width: 300px;
			height: fit-content;
			text-align: center;
			padding: 24px 24px 16px 24px;
			transform: translate(100%, -50%);
			right: -16px;
			top: 2px;
			outline: 0;

			h1 {
				margin: 0;
			}

			@media (min-width: $breakpoint-mid) {
				width: 360px;
			}
			button {
				height: auto;
				margin-top: 24px;
			}
		}
	}
}
</style>
